/**
 * FlashcardEditor.jsx
 * Admin content editor for lesson_type === 'flashcard'
 *
 * Content shape:
 *   { flashcards: [{ front, back, example }] }
 *
 * Props:
 *   value    — current content object
 *   onChange — (newContent) => void
 */


import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../contexts/LanguageContext';

const DEFAULT_CARD = { front: '', back: '', example: '' };

export default function FlashcardEditor({ value, onChange }) {
  const { t } = useLanguage();
  const cards = Array.isArray(value?.flashcards) ? value.flashcards : [];

  const update = (next) => onChange({ ...value, flashcards: next });

  const addCard = () => update([...cards, { ...DEFAULT_CARD }]);

  const removeCard = (idx) => update(cards.filter((_, i) => i !== idx));

  const updateCard = (idx, field, val) =>
    update(cards.map((c, i) => (i === idx ? { ...c, [field]: val } : c)));

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...cards];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    update(next);
  };

  const moveDown = (idx) => {
    if (idx === cards.length - 1) return;
    const next = [...cards];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    update(next);
  };

  return (
    <div className="space-y-3">
      {cards.length === 0 && (
        <p className="text-xs text-center text-[var(--text-muted)] py-4 border-2 border-dashed border-[var(--border-light)] rounded-xl">
          {t('lessonEditor.common.emptyList')}
        </p>
      )}
      {cards.map((card, idx) => (
        <div
          key={idx}
          className="bg-[var(--bg-app)] border-2 border-[var(--border-light)] rounded-xl p-4 space-y-2"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-[var(--text-muted)]">#{idx + 1}</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => moveUp(idx)}
                disabled={idx === 0}
                aria-label={t('lessonEditor.common.moveUp')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveDown(idx)}
                disabled={idx === cards.length - 1}
                aria-label={t('lessonEditor.common.moveDown')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => removeCard(idx)}
                aria-label={t('lessonEditor.flashcard.removeCard')}
                className="p-1 rounded hover:bg-red-50 text-[var(--text-muted)] hover:text-[var(--brand-danger)]"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.flashcard.front')} *
              </label>
              <Input
                value={card.front}
                onChange={(e) => updateCard(idx, 'front', e.target.value)}
                placeholder={t('lessonEditor.flashcard.frontPlaceholder')}
                className="rounded-xl border-2 border-[var(--border-light)] text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.flashcard.back')} *
              </label>
              <Input
                value={card.back}
                onChange={(e) => updateCard(idx, 'back', e.target.value)}
                placeholder={t('lessonEditor.flashcard.backPlaceholder')}
                className="rounded-xl border-2 border-[var(--border-light)] text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.flashcard.example')}
              </label>
              <Input
                value={card.example}
                onChange={(e) => updateCard(idx, 'example', e.target.value)}
                placeholder={t('lessonEditor.flashcard.examplePlaceholder')}
                className="rounded-xl border-2 border-[var(--border-light)] text-sm"
              />
            </div>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addCard}
        className="rounded-xl border-2 border-dashed border-[var(--border-heavy)] text-[var(--text-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] font-bold w-full"
      >
        <Plus size={14} className="mr-1" /> {t('lessonEditor.flashcard.addCard')}
      </Button>
    </div>
  );
}
